/* eslint-disable no-restricted-syntax */
import type { ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from '@/libs/tailwind';

export type ButtonVariant = 'primary' | 'secondary' | 'primary-destructive' | 'secondary-destructive' | 'tertiary' | 'ghost' | 'link' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'icon';

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  active?: boolean;
  onDark?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const variantStyles: Record<ButtonVariant, string> = {
  'primary': 'bg-interactive-primary text-fg-inverted hover:bg-interactive-primary-with-interactive-inverted-hover active:bg-interactive-primary-with-interactive-inverted-active aria-pressed:bg-interactive-primary-with-interactive-inverted-active disabled:bg-interactive-disabled-with-interactive-hover',
  'primary-destructive': 'bg-interactive-negative text-fg-inverted-static hover:bg-interactive-negative-with-interactive-hover active:bg-interactive-negative-with-interactive-active aria-pressed:bg-interactive-negative-with-interactive-active disabled:bg-bg-disabled',
  'secondary': 'bg-surface-primary border-0.5 border-fg-border text-fg-primary hover:bg-surface-primary-with-interactive-hover active:bg-surface-primary-with-interactive-active aria-pressed:bg-surface-primary-with-interactive-active disabled:bg-bg-disabled',
  'secondary-destructive': 'bg-surface-primary border-0.5 border-fg-border text-interactive-negative hover:bg-surface-primary-with-interactive-hover active:bg-surface-primary-with-interactive-active aria-pressed:bg-surface-primary-with-interactive-active disabled:bg-bg-disabled',
  'tertiary': 'text-fg-primary bg-transparent hover:bg-surface-primary-with-interactive-hover shadow-none active:bg-surface-primary-with-interactive-active aria-pressed:bg-surface-primary-with-interactive-active',
  'ghost': 'hover:bg-accent hover:text-accent-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground',
  'link': 'text-primary underline-offset-4 hover:underline',
  'icon': 'rounded-lg bg-black text-white border border-black hover:bg-primary/90 active:bg-primary/90 aria-pressed:bg-primary/90 disabled:bg-white disabled:border-gray-200 disabled:text-fg-primary',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-md px-3 text-body-sm!',
  md: 'h-[40px] px-[16px] py-[10px]',
  icon: 'size-9',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, loading, variant = 'primary', size = 'md', fullWidth, children, disabled, active, onDark, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        className={cn(
          'inline-flex cursor-pointer transition-colors rounded-lg font-medium whitespace-nowrap items-center justify-center gap-2 [&>svg]:size-4',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none text-fg-disabled',
          onDark && ' bg-interactive-secondary text-fg-primary hover:bg-interactive-inverted-hover hover:text-fg-inverted active:bg-interactive-inverted-active active:text-fg-inverted aria-pressed:bg-interactive-inverted-active aria-pressed:text-fg-inverted ',
          className,
        )}
        disabled={disabled}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
